import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Button,
    Dimmer,
    Form,
    Header,
    Loader,
    Message,
    Modal,
    Segment,
} from 'semantic-ui-react';

import * as Constant from '../../../Constants/Constant';
import {
    SYSTEM_ID_FOR_NEW,
    SYSTEM_TYPE_EXISTING,
    SYSTEM_TYPE_NEW,
    USB_KEY_ITEM_FIELDS,
} from '../../../Constants/gen1Constants';
import { useAppContext } from '../../../Contexts/AppContext';
import Gen1TrunkingOrderHeader from '../../../Models/ViewModels/Gen1TrunkingOrderHeader';
import Gen1TrunkingOrderItem from '../../../Models/ViewModels/Gen1TrunkingOrderItem';
import {
    buildGen1OrderPayload,
    getGen1ConfirmSubmitPayloadStatus,
    getGen1SavePayloadStatus,
} from '../../../Payloads/gen1OrderPayload';
import {
    getGen1ApplicationTypeLabel,
    includePurchasedRepeatersApplication,
} from '../gen1ApplicationType';
import {
    GEN1_APPLICATION_TYPE_CHANGE_CONFIRM_MESSAGE,
    GEN1_SYSTEM_TYPE_CHANGE_CONFIRM_MESSAGE,
    hasTrunkingInformationToReset,
    isSameApplicationType,
} from './state/gen1ApplicationTrunkingReset';
import {
    buildStateAfterApplicationTypeChange,
    buildStateAfterSystemTypeChange,
    buildFormFieldChangeAppStatePatch,
    buildCustomerAddressBookChangeAppStatePatch,
} from './state/gen1ApplicationStateBuilders';
import {
    isGen1Editable,
    isGen1Draft,
    isGen1NonDraftOrderSaveStatus,
} from '../gen1Status';
import {
    ADDITIONAL_NOTES_MAX_LENGTH,
    MAX_LENGTH,
    validateGen1Application,
    GEN1_VALIDATION_MODE,
} from './Gen1ApplicationValidation';
import Gen1ApplicationPreviewModal from './Gen1ApplicationPreviewModal';
import Gen1ApplicationTypeSelect from './Gen1ApplicationTypeSelect';
import Gen1ApplicationNewPurchaseTrunking from './Gen1ApplicationNewPurchaseTrunking';
import Gen1ApplicationPurchasedTrunking from './Gen1ApplicationPurchasedTrunking';
import { applyAppStatePatch } from './apply/gen1ApplicationApply';
import { useGen1OrderSerialSearch } from './hooks/useGen1OrderSerialSearch';
import { useRepeaterRebuild } from './hooks/useRepeaterRebuild';
import {
    buildAddRepeaterRowAppStatePatch,
    buildDeleteExchangeRepeaterPairAppStatePatch,
    buildDeleteManualRepeaterRowAppStatePatch,
    buildDeleteRemoveRepeaterRowAppStatePatch,
    buildRepeaterLineChangeAppStatePatch,
    getExchangePairLineSequenceNumbers,
} from './repeater/repeaterLineMutations';
import { buildRepeaterRebuildAppStatePatch } from './repeater/rebuildRepeaterSerialsFromTrunking';
import {
    buildClearOrderNumberValidationErrorAppStatePatch,
    buildDeleteSearchRepeaterLineAppStatePatch,
    buildOrderNumberValidationErrorAppStatePatch,
    buildRegisterSearchRepeaterAppStatePatch,
    buildSearchSuccessAppStatePatch,
} from './repeater/repeaterSearchMutations';
// 表示用 code / label（画面表示時のみ参照）
const USB_KEY_DISPLAY = {
    Grade1UnlimitedAccessUsbKeyQty: {
        code: 'KPGKEY01',
        label: 'Grade-1 Unlimited Access USB Key',
    },
    Grade2LimitedAccessUsbKeyQty: {
        code: 'KPGKEY02',
        label: 'Grade-2 Limited Access USB Key',
    },
    SystemManagerSoftwareWithGrade1KeyQty: {
        code: 'KPGKEYSM',
        label: 'System Manager Software with Grade-1 Key',
    },
    SystemManagerSoftwareQty: {
        code: 'KPG-110SMK',
        label: 'System Manager Software',
    },
};

const USB_KEY_MAX = {
    Grade1UnlimitedAccessUsbKeyQty: 10, // KPGKEY01
    Grade2LimitedAccessUsbKeyQty: 10, // KPGKEY02
    SystemManagerSoftwareWithGrade1KeyQty: 10, // KPGKEYSM
    SystemManagerSoftwareQty: 20, // KPG-110SMK
};

function resolveSelectedCustomerAddressBookId(order, addressBook) {
    const raw = Number(order?.CustomerAddressBookId);
    if (!Number.isFinite(raw) || raw === 0) {
        return 0;
    }
    const list = addressBook ?? [];
    const found = list.some((x) => Number(x?.Id) === raw);
    return found ? raw : -1;
}

/**
 * 整形済み payload から validateGen1Application に渡す state 形のオブジェクトを構築する。
 * 整形後の値でバリデーションを掛けるために使用する。
 * @param {Object} payload - buildGen1OrderPayload の戻り値
 * @param {Object} state - 現在の state（otherSystemId 等を参照）
 * @param {Object} masters - マスタ（バリデーション用）
 * @returns {Object} validateGen1Application(state) に渡すオブジェクト
 */
function buildStateForValidation(payload, state, masters) {
    const header = payload.Gen1TrunkingOrderHeader ?? {};
    const Gen1TrunkingOrderRepeaterSerials =
        payload.Gen1TrunkingOrderRepeaterSerials ?? [];

    return {
        ...state,
        gen1Order: {
            ...header,
            Gen1TrunkingOrderItem: payload.Gen1TrunkingOrderItem ?? {},
            Gen1TrunkingOrderRepeaterSerials,
        },
        otherSystemId: (state.otherSystemId ?? '').trim(),
        manufactureRepMaster: masters?.manufactureRepMaster ?? [],
        modelMasterForGen1: masters?.modelMasterForGen1 ?? [],
    };
}

/** Gen1TrunkingOrderHeader の初期値（フォーム用プレーンオブジェクト） */
function getDefaultGen1Order() {
    const order = new Gen1TrunkingOrderHeader();
    order.Country = 'US';
    order.SystemType = SYSTEM_TYPE_NEW;
    order.SystemId = SYSTEM_ID_FOR_NEW;
    return Object.assign({}, order);
}

/** Gen1TrunkingOrderItem の初期値（フォーム用プレーンオブジェクト） */
function getDefaultTrunkingOrderItem() {
    return Object.assign({}, new Gen1TrunkingOrderItem());
}

/** Repeater Details 1行に紐づくエラー（repeater_<LineSequenceNumber>_*）を削除 */
function omitRepeaterLineValidationKeys(errors, lineSequenceNumber) {
    if (lineSequenceNumber == null) return errors ?? {};
    const prefix = `repeater_${lineSequenceNumber}_`;
    const next = { ...(errors ?? {}) };
    for (const k of Object.keys(next)) {
        if (k.startsWith(prefix)) delete next[k];
    }
    return next;
}

const initialState = {
    gen1Order: getDefaultGen1Order(),

    // UI用のフィールド
    orderNumber: '',
    otherSystemId: '',

    addRepeater: true,

    /** 住所帳ドロップダウン選択値（0: ユーザー住所, -1: Other, それ以外: customerAddressBook.Id） */
    selectedCustomerAddressBookId: 0,
    /** Save this address チェック */
    isSaveAddressBook: true,

    /** 申請プレビューモーダル表示フラグ */
    previewModalOpen: false,
    /** 保存/申請完了モーダル表示フラグ */
    completeModalOpen: false,

    /** false = 種別選択画面（初回 or 変更）。true = 申請フォーム表示 */
    applicationTypeSelected: false,

    validationErrors: {},
};

function getBlankAppState() {
    return { ...initialState, gen1Order: getDefaultGen1Order() };
}

function buildInitialStateFromReducer(Reducer) {
    const base = getBlankAppState();
    const userInformation = Reducer?.userInformation;
    if (userInformation && Object.keys(userInformation).length > 0) {
        base.gen1Order = {
            ...base.gen1Order,
            ContactName: userInformation.UserName ?? base.gen1Order.ContactName,
            ContactEmail: userInformation.UserId ?? base.gen1Order.ContactEmail,
            AddressLineOne:
                userInformation.AddressLineOne ?? base.gen1Order.AddressLineOne,
            AddressLineTwo:
                userInformation.AddressLineTwo ?? base.gen1Order.AddressLineTwo,
            AddressLineThree:
                userInformation.AddressLineThree ??
                base.gen1Order.AddressLineThree,
            City: userInformation.City ?? base.gen1Order.City,
            State: (Reducer?.stateMaster || []).some(
                (x) => x.Code === userInformation.State
            )
                ? userInformation.State
                : base.gen1Order.State,
            Zip: userInformation.Zip ?? base.gen1Order.Zip,
            Country: userInformation.Country ?? base.gen1Order.Country,
            Telephone: userInformation.Telephone ?? base.gen1Order.Telephone,
        };
    }
    return base;
}

function deriveAddRepeaterFromFetchedOrder(order) {
    if (includePurchasedRepeatersApplication(order)) {
        const item = order?.Gen1TrunkingOrderItem ?? {};
        return (
            (Number(item.RepeaterQty) || 0) > 0 ||
            (Number(item.MultiSiteNetworkingQty) || 0) > 0
        );
    }
    return true;
}

function resolveAddRepeaterForApplicationType(order) {
    if (includePurchasedRepeatersApplication(order)) {
        return order?.SystemType === SYSTEM_TYPE_NEW;
    }
    return true;
}

function buildUiFieldsFromFetchedOrder(order) {
    const serials = order?.Gen1TrunkingOrderRepeaterSerials ?? [];
    const firstOrderNumber =
        serials
            .map((s) => s?.OrderNumber)
            .find((n) => n != null && String(n).trim() !== '') ?? '';
    const otherSystemId =
        serials.find(
            (s) =>
                s?.ExchangeSystemId && String(s.ExchangeSystemId).trim() !== ''
        )?.ExchangeSystemId ?? '';
    const addRepeater = deriveAddRepeaterFromFetchedOrder(order);
    return {
        orderNumber: String(firstOrderNumber || ''),
        otherSystemId: String(otherSystemId || ''),
        addRepeater,
    };
}

function buildAppStateFromFetchedOrder(order, customerAddressBook) {
    const ui = buildUiFieldsFromFetchedOrder(order);
    return {
        ...getBlankAppState(),
        gen1Order: Object.assign({}, order),
        orderNumber: ui.orderNumber,
        otherSystemId: ui.otherSystemId,
        addRepeater: ui.addRepeater,
        selectedCustomerAddressBookId: resolveSelectedCustomerAddressBookId(
            order,
            customerAddressBook
        ),
        applicationTypeSelected: true,
    };
}

function normalizeModelKey(value) {
    return String(value ?? '')
        .trim()
        .replace(/\s+/g, '')
        .toUpperCase();
}

function normalizeMasterCode(value) {
    return String(value ?? '').trim().toUpperCase();
}

function buildStateOptionValue(countryCode, stateCode) {
    const normalizedCountryCode = normalizeMasterCode(countryCode);
    const normalizedStateCode = normalizeMasterCode(stateCode);
    if (!normalizedCountryCode || !normalizedStateCode) {
        return String(stateCode ?? '').trim();
    }
    return `${normalizedCountryCode}_${normalizedStateCode}`;
}

function extractStateCodeFromOptionValue(optionValue) {
    const raw = String(optionValue ?? '').trim();
    if (!raw) return '';

    const separatorIndex = raw.indexOf('_');
    if (separatorIndex === -1) {
        return raw;
    }
    return raw.slice(separatorIndex + 1);
}

function isStateMatchedToCountry(stateCode, countryCode, stateMaster) {
    const normalizedStateCode = normalizeMasterCode(stateCode);
    const normalizedCountryCode = normalizeMasterCode(countryCode);
    if (!normalizedStateCode || !normalizedCountryCode) return true;

    return (stateMaster ?? []).some(
        (x) =>
            normalizeMasterCode(x?.Code) === normalizedStateCode &&
            normalizeMasterCode(x?.CountryCode) === normalizedCountryCode
    );
}

function resolveNormalizedModelNameFromGen1ItemMaster(
    baseModel,
    marketCode,
    modelMasterForGen1
) {
    const base = String(baseModel ?? '').trim();
    const market = String(marketCode ?? '').trim();
    if (!base) return '';
    if (!market) return base;

    const combined = `${base}${market}`;
    const combinedKey = normalizeModelKey(combined);
    const matched = (modelMasterForGen1 ?? []).find(
        (x) => normalizeModelKey(x?.ModelName) === combinedKey
    );
    return matched?.ModelName ?? combined;
}

function normalizeFetchedRepeaterSerialsByGen1ItemMaster(
    serials,
    modelMasterForGen1
) {
    return (serials ?? []).map((serial) => ({
        ...serial,
        BaseModel: resolveNormalizedModelNameFromGen1ItemMaster(
            serial?.BaseModel,
            serial?.MarketCode,
            modelMasterForGen1
        ),
    }));
}

export default function Gen1Application(props) {
    const { editOrderId = 0, onCancelToDetail } = props || {};
    const { Reducer, Actions } = useAppContext();
    const isEdit = (Number(editOrderId) || 0) >= 1;
    // 初回だけ決まる初期表示:
    // - 編集時（editOrderId >= 1）→ いったん空。すぐあとに、その注文 ID に紐づく保存済みの内容だけが画面に出る（ログイン者の住所などは自動では入れない）。
    // - 新規作成時（editOrderId = 0）→ ログイン者の連絡先・住所などがあらかじめ入る。州コードはマスタにあるものだけ採用する。
    const [state, setState] = useState(() =>
        isEdit ? getBlankAppState() : buildInitialStateFromReducer(Reducer)
    );
    const [editFetchLoading, setEditFetchLoading] = useState(false);
    const [editFetchError, setEditFetchError] = useState('');
    const [customStateOptions, setCustomStateOptions] = useState([]);
    const [customCountryOptions, setCustomCountryOptions] = useState([]);
    /** Sales Order Number (SO#) + Search 行（Search 用バリデーション時にスクロール） */
    const orderNumberSearchSectionRef = useRef(null);

    // Sales Order Number (SO#) 入力欄のバリデーションエラーを表示する
    const handleOrderNumberValidationError = useCallback((message) => {
        setState((prev) =>
            applyAppStatePatch(
                prev,
                buildOrderNumberValidationErrorAppStatePatch(message)
            )
        );
    }, []);

    // Sales Order Number (SO#) 入力欄のバリデーションエラーをクリアする
    const handleClearOrderNumberValidationError = useCallback(() => {
        setState((prev) =>
            applyAppStatePatch(
                prev,
                buildClearOrderNumberValidationErrorAppStatePatch()
            )
        );
    }, []);

    // Search 成功時: 検索結果を state に反映（候補の反映まで。Register は未）
    const handleSearchSuccessRepeater = useCallback((searchPatch) => {
        setState((prev) =>
            applyAppStatePatch(
                prev,
                buildSearchSuccessAppStatePatch(searchPatch)
            )
        );
    }, []);

    /**
     * Register 時: 選択した検索結果を明細に確定登録（Search 由来 ADD を置換）
     * @param {string} orderNumber - Sales Order Number (SO#)
     * @param {Array} items - 選択されたリピーターアイテムの配列
     */
    const handleRegisterSearchRepeater = useCallback((orderNumber, items) => {
        setState((prev) =>
            applyAppStatePatch(
                prev,
                buildRegisterSearchRepeaterAppStatePatch(
                    prev,
                    orderNumber,
                    items
                )
            )
        );
    }, []);

    /**
     * Delete 時: 選択した明細行を削除（Search 由来 ADD を除去）
     * @param {number} lineSequenceNumber - 削除対象の行番号
     * @returns {{ nextState: object, deletedLine: object | null }} - 更新後の state と削除行の情報
     */
    const handleDeleteSearchRepeaterLine = useCallback((lineSequenceNumber) => {
        let deleteResult = null;
        setState((prev) => {
            const { patch, deletedLine } =
                buildDeleteSearchRepeaterLineAppStatePatch(
                    prev,
                    lineSequenceNumber
                );
            const nextState = {
                ...applyAppStatePatch(prev, patch),
                validationErrors: omitRepeaterLineValidationKeys(
                    prev.validationErrors,
                    lineSequenceNumber
                ),
            };
            deleteResult = { nextState, deletedLine };
            return nextState;
        });
        return deleteResult;
    }, []);

    // Repeater Details を再構築する（useRepeaterRebuild の useEffect から呼ばれる）
    const handleRepeaterRebuild = useCallback(() => {
        setState((prev) =>
            applyAppStatePatch(prev, buildRepeaterRebuildAppStatePatch(prev))
        );
    }, []);

    // Repeater Details のセル編集（Purchased/New Purchase の Trunking UI から呼ばれる）
    const handleRepeaterLineChange = useCallback(
        (lineSequenceNumber, field, value) => {
            setState((prev) => ({
                ...applyAppStatePatch(
                    prev,
                    buildRepeaterLineChangeAppStatePatch(
                        prev,
                        lineSequenceNumber,
                        field,
                        value
                    )
                ),
                validationErrors: omitRepeaterLineValidationKeys(
                    prev.validationErrors,
                    lineSequenceNumber
                ),
            }));
        },
        []
    );

    // Repeater Details に 1 行追加（Add Row。Purchased/New Purchase の Trunking UI から呼ばれる）
    const handleAddRepeaterRow = useCallback(() => {
        setState((prev) =>
            applyAppStatePatch(prev, buildAddRepeaterRowAppStatePatch(prev))
        );
    }, []);

    // Repeater Details の手動 ADD 行を 1 行削除（Purchased/New Purchase の Trunking UI から呼ばれる）
    const handleDeleteManualRepeaterRow = useCallback((lineSequenceNumber) => {
        setState((prev) => ({
            ...applyAppStatePatch(
                prev,
                buildDeleteManualRepeaterRowAppStatePatch(
                    prev,
                    lineSequenceNumber
                )
            ),
            validationErrors: omitRepeaterLineValidationKeys(
                prev.validationErrors,
                lineSequenceNumber
            ),
        }));
    }, []);

    // Repeater Details の Remove 単体行を 1 行削除（Purchased Trunking UI から呼ばれる）
    const handleDeleteRemoveRepeaterRow = useCallback((lineSequenceNumber) => {
        setState((prev) => ({
            ...applyAppStatePatch(
                prev,
                buildDeleteRemoveRepeaterRowAppStatePatch(
                    prev,
                    lineSequenceNumber
                )
            ),
            validationErrors: omitRepeaterLineValidationKeys(
                prev.validationErrors,
                lineSequenceNumber
            ),
        }));
    }, []);

    // Repeater Details の Exchange ペアを 1 つ削除（Purchased Trunking UI から呼ばれる）
    const handleDeleteExchangeRepeaterPair = useCallback((pairIndex) => {
        setState((prev) => {
            const serials =
                prev.gen1Order?.Gen1TrunkingOrderRepeaterSerials ?? [];
            const affectedLineSeqs = getExchangePairLineSequenceNumbers(
                serials,
                pairIndex
            );
            let validationErrors = prev.validationErrors;
            for (const lineSeq of affectedLineSeqs) {
                validationErrors = omitRepeaterLineValidationKeys(
                    validationErrors,
                    lineSeq
                );
            }
            return {
                ...applyAppStatePatch(
                    prev,
                    buildDeleteExchangeRepeaterPairAppStatePatch(
                        prev,
                        pairIndex
                    )
                ),
                validationErrors,
            };
        });
    }, []);

    useRepeaterRebuild({
        addRepeater: state.addRepeater,
        otherSystemId: state.otherSystemId ?? '',
        gen1Order: state.gen1Order,
        onRepeaterRebuild: handleRepeaterRebuild,
    });

    const {
        orderSearchResult,
        orderSearchLoading,
        orderSearchError,
        orderSearchModalOpen,
        onSearchClick,
        onOrderSearchShowModalClick,
        onOrderSearchModalClose,
        onOrderSearchSelectAllChange,
        onOrderSearchRowToggle,
        onOrderSearchRegisterClick,
        onDeleteSearchRepeaterRow,
        resetOrderSearchState,
    } = useGen1OrderSerialSearch({
        orderNumber: state.orderNumber ?? '',
        editOrderId: Number(editOrderId) || 0,
        repeaterSerials:
            state.gen1Order?.Gen1TrunkingOrderRepeaterSerials ?? [],
        orderNumberSearchSectionRef,
        fetchOrderSerialsByOrderNumber: Actions.fetchOrderSerialsByOrderNumber,
        csrftoken: Reducer.csrftoken,
        onOrderNumberValidationError: handleOrderNumberValidationError,
        onClearOrderNumberValidationError:
            handleClearOrderNumberValidationError,
        onSearchSuccessRepeater: handleSearchSuccessRepeater,
        onRegisterSearchRepeater: handleRegisterSearchRepeater,
        onDeleteSearchRepeaterLine: handleDeleteSearchRepeaterLine,
    });
    const prevEditOrderIdRef = useRef(editOrderId);
    /** 種別選択フロー: 'default'（通常）| 'change'（フォームから種別変更中）。'default' は初回限定ではなく change 終了後も含む */
    const typeSelectPurposeRef = useRef('default');

    // 新規作成: ログイン者情報のプリフィル
    useEffect(() => {
        const idNum = Number(editOrderId) || 0;
        const prevIdNum = Number(prevEditOrderIdRef.current) || 0;
        prevEditOrderIdRef.current = editOrderId;
        if (!Reducer.csrftoken || idNum >= 1) return;

        setEditFetchLoading(false);
        setEditFetchError('');
        const cameFromEdit = prevIdNum >= 1 && idNum < 1;
        let reinitialized = false;
        setState((prev) => {
            if (
                !cameFromEdit &&
                (prev.applicationTypeSelected ||
                    typeSelectPurposeRef.current === 'change')
            ) {
                return prev;
            }
            typeSelectPurposeRef.current = 'default';
            reinitialized = true;
            return buildInitialStateFromReducer(Reducer);
        });
        if (reinitialized) {
            resetOrderSearchState();
        }
    }, [editOrderId, Reducer.csrftoken, resetOrderSearchState]);

    // 編集: 注文 ID で最新を取得し、フォーム初期値として反映する
    useEffect(() => {
        const idNum = Number(editOrderId) || 0;
        if (!Reducer.csrftoken || idNum < 1) return;

        setState(getBlankAppState());
        resetOrderSearchState();
        setEditFetchLoading(true);
        setEditFetchError('');
        Actions.fetchGen1OrderById(Reducer.csrftoken, idNum)
            .then((data) => {
                const header = new Gen1TrunkingOrderHeader({
                    ...(data?.Gen1TrunkingOrderHeader ?? data),
                    Gen1TrunkingOrderItem:
                        data?.Gen1TrunkingOrderItem ?? undefined,
                    Gen1TrunkingOrderRepeaterSerials:
                        normalizeFetchedRepeaterSerialsByGen1ItemMaster(
                            data?.Gen1TrunkingOrderRepeaterSerials,
                            Reducer.modelMasterForGen1
                        ),
                });
                setState(
                    buildAppStateFromFetchedOrder(
                        header,
                        Reducer.customerAddressBook
                    )
                );
                resetOrderSearchState();
                typeSelectPurposeRef.current = 'default';
            })
            .catch((e) => {
                setEditFetchError(
                    e?.message ?? 'An error occurred while loading the order.'
                );
            })
            .finally(() => setEditFetchLoading(false));
    }, [
        editOrderId,
        Reducer.csrftoken,
        Reducer.modelMasterForGen1,
        resetOrderSearchState,
    ]);

    // 編集: 住所帳マスタの遅延読込後に Select address の選択値だけ補正する
    useEffect(() => {
        const idNum = Number(editOrderId) || 0;
        if (idNum < 1 || editFetchLoading) return;

        setState((prev) => {
            const nextId = resolveSelectedCustomerAddressBookId(
                prev.gen1Order,
                Reducer.customerAddressBook
            );
            if (prev.selectedCustomerAddressBookId === nextId) {
                return prev;
            }
            return { ...prev, selectedCustomerAddressBookId: nextId };
        });
    }, [editOrderId, Reducer.customerAddressBook, editFetchLoading]);

    /** 送信成功時に Reducer の gen1Order でローカル state を同期する */
    useEffect(() => {
        if (Reducer.gen1OrderSaveSuccess && Reducer.gen1Order != null) {
            setState((prev) => ({
                ...prev,
                gen1Order: Reducer.gen1Order,
                applicationTypeSelected: true,
                validationErrors: {},
            }));
        }
    }, [Reducer.gen1OrderSaveSuccess, Reducer.gen1Order]);

    const prevGen1OrderSaveSuccess = useRef(Reducer.gen1OrderSaveSuccess);
    /** 保存/申請成功時に完了モーダルを開く */
    useEffect(() => {
        if (
            prevGen1OrderSaveSuccess.current === false &&
            Reducer.gen1OrderSaveSuccess === true
        ) {
            setState((prev) => ({ ...prev, completeModalOpen: true }));
        }
        prevGen1OrderSaveSuccess.current = Reducer.gen1OrderSaveSuccess;
    }, [Reducer.gen1OrderSaveSuccess]);

    /** Select address 変更時: CustomerAddressBook / userInformation から住所を反映 */
    function onCustomerAddressBookChange(evn, data) {
        const selectedId = data.value;
        if (state.selectedCustomerAddressBookId === selectedId) return;

        const address =
            selectedId === 0
                ? Reducer.userInformation
                : (Reducer.customerAddressBook || []).find(
                      (x) => x.Id == selectedId
                  );

        setState((prev) =>
            applyAppStatePatch(
                prev,
                buildCustomerAddressBookChangeAppStatePatch(
                    prev,
                    selectedId,
                    address
                )
            )
        );
    }

    function onFormChange(evn, data) {
        //  State の場合は、value（CountryCode_StateCode）を州コードに変換してから state に反映する
        const normalizedData =
            data?.name === 'State'
                ? {
                      ...data,
                      value: extractStateCodeFromOptionValue(data?.value),
                  }
                : data;

        setState((prev) => {
            const basePatch = buildFormFieldChangeAppStatePatch(
                prev,
                normalizedData
            );
            const nextState = applyAppStatePatch(prev, basePatch);

            if (normalizedData?.name !== 'Country') {
                return nextState;
            }

            const nextCountry = nextState.gen1Order?.Country;
            const nextStateCode = nextState.gen1Order?.State;
            // Country変更後もStateがその国に属する場合はそのまま保持する。
            if (
                isStateMatchedToCountry(
                    nextStateCode,
                    nextCountry,
                    Reducer.stateMaster
                )
            ) {
                return nextState;
            }

            // 不整合な組み合わせになる場合のみStateをクリアする。
            const clearStatePatch = buildFormFieldChangeAppStatePatch(
                nextState,
                {
                    name: 'State',
                    value: '',
                }
            );
            return applyAppStatePatch(nextState, clearStatePatch);
        });
    }

    /** System Type 変更時（Customer Information・USB Keys・Additional Notes 以外を初期化） */
    function onSystemTypeChange(evn, data) {
        const value = data.value;
        if (value === state.gen1Order?.SystemType) return;
        if (hasTrunkingInformationToReset(state)) {
            const confirmed = window.confirm(
                GEN1_SYSTEM_TYPE_CHANGE_CONFIRM_MESSAGE
            );
            if (!confirmed) return;
        }
        setState((prev) =>
            buildStateAfterSystemTypeChange(prev, value, {
                getDefaultGen1Order,
                getDefaultTrunkingOrderItem,
                usbKeyItemFields: USB_KEY_ITEM_FIELDS,
                resolveAddRepeaterForApplicationType,
            })
        );
        resetOrderSearchState();
    }

    /** 初回選択 / 種別変更（Continue 時。変更時は別種別のみ confirm） */
    function handleApplicationTypeSelect(includePurchasedRepeaters) {
        const isChangeFlow = typeSelectPurposeRef.current === 'change';
        if (isChangeFlow) {
            if (
                isSameApplicationType(
                    state.gen1Order?.IncludePurchasedRepeaters,
                    includePurchasedRepeaters
                )
            ) {
                typeSelectPurposeRef.current = 'default';
                setState((prev) => ({
                    ...prev,
                    applicationTypeSelected: true,
                }));
                return;
            }
            const confirmed = window.confirm(
                GEN1_APPLICATION_TYPE_CHANGE_CONFIRM_MESSAGE
            );
            if (!confirmed) return;
        }
        typeSelectPurposeRef.current = 'default';
        setState((prev) =>
            buildStateAfterApplicationTypeChange(
                prev,
                includePurchasedRepeaters,
                {
                    getDefaultGen1Order,
                    getDefaultTrunkingOrderItem,
                    usbKeyItemFields: USB_KEY_ITEM_FIELDS,
                    resolveAddRepeaterForApplicationType,
                }
            )
        );
        resetOrderSearchState();
    }

    function onStartApplicationTypeChange() {
        typeSelectPurposeRef.current = 'change';
        setState((prev) => ({ ...prev, applicationTypeSelected: false }));
    }

    function onCancelApplicationTypeChange() {
        typeSelectPurposeRef.current = 'default';
        setState((prev) => ({ ...prev, applicationTypeSelected: true }));
    }

    /** Confirm 意図で送るペイロードを組み立て、同じ条件でバリデーションする */
    function buildConfirmIntentPayloadAndErrors() {
        const status = getGen1ConfirmSubmitPayloadStatus(state.gen1Order);
        const payload = buildGen1OrderPayload(state, {
            SYSTEM_TYPE_NEW,
            SYSTEM_ID_FOR_NEW,
            status,
        });
        const stateForValidation = buildStateForValidation(payload, state, {
            manufactureRepMaster: Reducer.manufactureRepMaster,
            modelMasterForGen1: Reducer.modelMasterForGen1,
        });
        const validationErrors = validateGen1Application(stateForValidation, {
            mode: GEN1_VALIDATION_MODE.ORDERED,
        });
        return { payload, validationErrors };
    }

    /** Save: 下書き保存 */
    function onSaveClick() {
        const payload = buildGen1OrderPayload(state, {
            SYSTEM_TYPE_NEW,
            SYSTEM_ID_FOR_NEW,
            status: getGen1SavePayloadStatus(),
        });
        const stateForValidation = buildStateForValidation(payload, state, {
            manufactureRepMaster: Reducer.manufactureRepMaster,
            modelMasterForGen1: Reducer.modelMasterForGen1,
        });
        const validationErrors = validateGen1Application(stateForValidation, {
            mode: GEN1_VALIDATION_MODE.DRAFT,
        });
        if (Object.keys(validationErrors).length > 0) {
            setState((prev) => ({ ...prev, validationErrors }));
            return;
        }
        Actions.saveGen1Order(
            Reducer.csrftoken,
            payload,
            state.gen1Order?.id,
            state.gen1Order
        );
    }

    /** Confirm: バリデーション後、プレビューモーダルを開く */
    function onConfirmClick() {
        const { validationErrors } = buildConfirmIntentPayloadAndErrors();
        if (Object.keys(validationErrors).length > 0) {
            setState((prev) => ({ ...prev, validationErrors }));
            return;
        }
        setState((prev) => ({
            ...prev,
            validationErrors: {},
            previewModalOpen: true,
        }));
    }

    /** プレビュー画面の Order ボタン押下時: 申請を送信（確定）する */
    function handleSubmitOrderFromPreview() {
        const { payload, validationErrors } =
            buildConfirmIntentPayloadAndErrors();
        if (Object.keys(validationErrors).length > 0) {
            setState((prev) => ({ ...prev, validationErrors }));
            return;
        }
        setState((prev) => ({ ...prev, previewModalOpen: false }));
        Actions.saveGen1Order(
            Reducer.csrftoken,
            payload,
            state.gen1Order?.id,
            state.gen1Order
        );
    }

    /** 完了モーダルを閉じ、成功フラグをクリアする */
    function onCloseCompleteModal() {
        const lastSaveStatus = Reducer.gen1OrderSaveLastStatus;
        Actions.clearGen1OrderSaveSuccess();
        if (isGen1NonDraftOrderSaveStatus(lastSaveStatus)) {
            setState(buildInitialStateFromReducer(Reducer));
            return;
        }
        setState((prev) => ({ ...prev, completeModalOpen: false }));
    }

    /** 完了モーダルから History へ遷移する */
    function onGoToHistoryFromComplete() {
        Actions.clearGen1OrderSaveSuccess();
        setState((prev) => ({ ...prev, completeModalOpen: false }));
        Actions.setTabStatus(Constant.REPAIRREQUEST_STAT_GEN1_HISTORY);
    }

    const notesLength = (state.gen1Order?.AdditionalNotes ?? '').length;

    const currentStateVal = state.gen1Order?.State ?? '';
    const normalizedCountryCode = normalizeMasterCode(
        state.gen1Order?.Country
    );
    const filteredStateMaster = (Reducer.stateMaster || []).filter(
        (x) =>
            !normalizedCountryCode ||
            normalizeMasterCode(x?.CountryCode) === normalizedCountryCode
    );
    const masterStateOptions = filteredStateMaster.map((x) => {
        const optionValue = buildStateOptionValue(x?.CountryCode, x?.Code);
        return {
            key: optionValue,
            value: optionValue,
            text: `${x.Code} - ${x.Name}`,
            stateCode: x.Code,
        };
    });
    const selectedMasterStateOption = masterStateOptions.find(
        (x) => x.stateCode === currentStateVal
    );
    const hasMasterStateOption = !!selectedMasterStateOption;
    const currentStateDropdownValue =
        selectedMasterStateOption?.value ?? currentStateVal;
    const stateOptions = [
        ...masterStateOptions,
        ...customStateOptions,
        ...(currentStateVal &&
        !hasMasterStateOption &&
        !customStateOptions.some((x) => x.value === currentStateVal)
            ? [
                  {
                      key: currentStateVal,
                      value: currentStateVal,
                      text: currentStateVal,
                  },
              ]
            : []),
    ];

    function onStateAddItem(evn, { value }) {
        setCustomStateOptions((prev) => {
            if (prev.some((x) => x.value === value)) return prev;
            return [...prev, { key: value, value, text: value }];
        });
    }

    const currentCountryVal = state.gen1Order?.Country ?? '';
    const countryMaster = Reducer.countryMaster || [];
    const masterCountryOptions = countryMaster.map((x) => ({
        key: x.Code,
        value: x.Code,
        text: `${x.Code} - ${x.Name}`,
    }));
    const countryOptions = [
        ...masterCountryOptions,
        ...customCountryOptions,
        ...(currentCountryVal &&
        !masterCountryOptions.some((x) => x.value === currentCountryVal) &&
        !customCountryOptions.some((x) => x.value === currentCountryVal)
            ? [
                  {
                      key: currentCountryVal,
                      value: currentCountryVal,
                      text: currentCountryVal,
                  },
              ]
            : []),
    ];

    function onCountryAddItem(evn, { value }) {
        setCustomCountryOptions((prev) => {
            if (prev.some((x) => x.value === value)) return prev;
            return [...prev, { key: value, value, text: value }];
        });
    }

    const manufacturersRepOptions = [
        { key: '', value: '', text: 'Select Manufacturers Rep' },
        ...(Reducer.manufactureRepMaster || []).map((x) => ({
            key: x.Id,
            value: x.ManufactureRep,
            text: x.ManufactureRep,
        })),
    ];

    const userInfo = Reducer.userInformation ?? {};
    const addressBook = Reducer.customerAddressBook ?? [];
    const customerAddressBookOptions = [
        {
            key: 0,
            value: 0,
            text: `${userInfo.AddressLineOne} ${userInfo.City}, ${userInfo.State}`,
        },
        ...addressBook.map((x) => ({
            key: x.Id,
            value: x.Id,
            text: `${x.AddressLineOne} ${x.City}, ${x.State}`,
        })),
        { key: -1, value: -1, text: 'Other (please specify)' },
    ];

    /** 画面上部に載せる集約エラー（インライン error のみの項目は除外） */
    const validationSummaryMessages = [
        ...new Set(
            Object.entries(state.validationErrors)
                .filter(([key, msg]) => msg && key !== 'orderNumber')
                .map(([, msg]) => msg)
        ),
    ];
    const errorMessage =
        validationSummaryMessages.length > 0 ? (
            <Message error>
                {validationSummaryMessages.map((msg, i) => (
                    <span key={i}>
                        {msg}
                        <br />
                    </span>
                ))}
            </Message>
        ) : null;

    const isPurchasedApplicationType = includePurchasedRepeatersApplication(
        state.gen1Order
    );
    const canShowSaveButton = isGen1Draft(state.gen1Order?.Status);
    const canShowConfirmButton = isGen1Editable(state.gen1Order?.Status);
    const showTypeSelect = !state.applicationTypeSelected && !editFetchLoading;
    const typeSelectMode = typeSelectPurposeRef.current;
    const canChangeApplicationType =
        state.applicationTypeSelected &&
        (state.gen1Order?.Status == null ||
            isGen1Draft(state.gen1Order?.Status));
    const applicationTypeLabel = getGen1ApplicationTypeLabel(
        state.gen1Order?.IncludePurchasedRepeaters
    );

    if (showTypeSelect) {
        return (
            <>
                {Reducer.gen1MasterFetchingError && (
                    <Message error>
                        Failed to load master data. Please refresh the page or
                        try again later.
                    </Message>
                )}
                <Gen1ApplicationTypeSelect
                    mode={typeSelectMode}
                    currentIncludePurchasedRepeaters={
                        state.gen1Order?.IncludePurchasedRepeaters
                    }
                    onSelect={handleApplicationTypeSelect}
                    onCancel={
                        typeSelectMode === 'change'
                            ? onCancelApplicationTypeChange
                            : undefined
                    }
                    disabled={Reducer.gen1MasterFetchingError}
                />
            </>
        );
    }
    const showActionButtons =
        (isEdit && !!onCancelToDetail) ||
        canShowSaveButton ||
        canShowConfirmButton;
    const actionButtonsDisabled =
        Reducer.gen1OrderSaveFetching || Reducer.gen1MasterFetchingError;
    const actionButtons = showActionButtons ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {isEdit && onCancelToDetail ? (
                <Button
                    type="button"
                    content="Cancel"
                    icon="arrow left"
                    labelPosition="left"
                    onClick={onCancelToDetail}
                />
            ) : null}
            {canShowSaveButton ? (
                <Button
                    content="Save"
                    icon="save"
                    color="blue"
                    disabled={actionButtonsDisabled}
                    onClick={onSaveClick}
                />
            ) : null}
            {canShowConfirmButton ? (
                <Button
                    content="Confirm"
                    icon="check"
                    color="blue"
                    disabled={actionButtonsDisabled}
                    onClick={onConfirmClick}
                />
            ) : null}
        </div>
    ) : null;

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: '0.5rem',
                }}
            >
                <Header
                    as="h2"
                    icon="edit"
                    content="Gen1 Trunking Order Form - Application"
                    style={{ marginBottom: 0 }}
                />
                {actionButtons}
            </div>
            {(editFetchLoading || editFetchError) && (
                <>
                    {editFetchError && (
                        <Message error>{editFetchError}</Message>
                    )}
                    <Dimmer active={editFetchLoading} page>
                        <Loader>Loading order…</Loader>
                    </Dimmer>
                </>
            )}
            {errorMessage}
            {Reducer.gen1MasterFetchingError && (
                <Message error>
                    Failed to load master data. Please refresh the page or try
                    again later.
                </Message>
            )}
            {Reducer.gen1OrderSaveError &&
                Reducer.gen1OrderSaveErrorMessage && (
                    <Message error style={{ whiteSpace: 'pre-line' }}>
                        {Reducer.gen1OrderSaveErrorMessage}
                    </Message>
                )}

            <Segment>
                <Header
                    icon="building"
                    content="Customer Information"
                    dividing
                />
                <Form>
                    <Form.Group widths="equal">
                        <Form.Input
                            required
                            label="Contact Name"
                            name="ContactName"
                            value={state.gen1Order?.ContactName ?? ''}
                            onChange={onFormChange}
                            error={!!state.validationErrors.contactName}
                        />
                        <Form.Input
                            required
                            label="Contact Email"
                            name="ContactEmail"
                            type="email"
                            value={state.gen1Order?.ContactEmail ?? ''}
                            onChange={onFormChange}
                            error={!!state.validationErrors.contactEmail}
                        />
                    </Form.Group>
                    <Form.Dropdown
                        search
                        selection
                        label="Select address"
                        width={8}
                        options={customerAddressBookOptions}
                        value={state.selectedCustomerAddressBookId}
                        onChange={onCustomerAddressBookChange}
                        placeholder="Select address"
                    />
                    <Form.Input
                        required
                        label="Address"
                        name="AddressLineOne"
                        placeholder="Street address"
                        value={state.gen1Order?.AddressLineOne ?? ''}
                        onChange={onFormChange}
                        error={!!state.validationErrors.addressLineOne}
                    />
                    <Form.Input
                        label=""
                        name="AddressLineTwo"
                        placeholder=""
                        value={state.gen1Order?.AddressLineTwo ?? ''}
                        onChange={onFormChange}
                        error={!!state.validationErrors.addressLineTwo}
                    />
                    <Form.Input
                        label=""
                        name="AddressLineThree"
                        placeholder=""
                        value={state.gen1Order?.AddressLineThree ?? ''}
                        onChange={onFormChange}
                        error={!!state.validationErrors.addressLineThree}
                    />
                    <Form.Group>
                        <Form.Input
                            required
                            label="City"
                            name="City"
                            width={4}
                            value={state.gen1Order?.City ?? ''}
                            onChange={onFormChange}
                            error={!!state.validationErrors.city}
                        />
                        <Form.Dropdown
                            required
                            search
                            selection
                            allowAdditions
                            additionLabel=""
                            label="State"
                            name="State"
                            width={4}
                            placeholder="Select or enter State"
                            options={stateOptions}
                            value={currentStateDropdownValue}
                            onChange={onFormChange}
                            onAddItem={onStateAddItem}
                            error={!!state.validationErrors.state}
                        />
                        <Form.Input
                            required
                            label="Zip/Postal Code"
                            name="Zip"
                            width={4}
                            value={state.gen1Order?.Zip ?? ''}
                            onChange={onFormChange}
                            error={!!state.validationErrors.zip}
                        />
                        <Form.Dropdown
                            required
                            search
                            selection
                            allowAdditions
                            additionLabel=""
                            label="Country"
                            name="Country"
                            width={4}
                            placeholder="Select or enter Country"
                            options={countryOptions}
                            value={state.gen1Order?.Country ?? ''}
                            onChange={onFormChange}
                            onAddItem={onCountryAddItem}
                            error={!!state.validationErrors.country}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Input
                            required
                            label="Telephone"
                            name="Telephone"
                            width={6}
                            value={state.gen1Order?.Telephone ?? ''}
                            onChange={onFormChange}
                            error={!!state.validationErrors.telephone}
                        />
                        <Form.Dropdown
                            required
                            search
                            selection
                            label="Manufacturers Rep"
                            name="ManufactureRep"
                            width={6}
                            placeholder="Select Manufacturers Rep"
                            options={manufacturersRepOptions}
                            value={state.gen1Order?.ManufactureRep ?? ''}
                            onChange={onFormChange}
                            error={!!state.validationErrors.manufactureRep}
                        />
                    </Form.Group>
                    <Form.Checkbox
                        label="Save this address"
                        name="isSaveAddressBook"
                        checked={state.isSaveAddressBook}
                        onChange={onFormChange}
                    />
                </Form>
            </Segment>

            <Segment>
                <Header
                    icon="wifi"
                    content="Gen1 Trunking Information"
                    dividing
                />
                <Form>
                    <Form.Field>
                        <label>Application Type</label>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 12,
                            }}
                        >
                            <span style={{ fontSize: '1.05em' }}>
                                <strong>{applicationTypeLabel}</strong>
                            </span>
                            {canChangeApplicationType ? (
                                <Button
                                    type="button"
                                    size="small"
                                    content="Change application type"
                                    onClick={onStartApplicationTypeChange}
                                />
                            ) : (
                                <span style={{ color: '#666' }}>
                                    Cannot be changed after submission
                                    (Ordered).
                                </span>
                            )}
                        </div>
                    </Form.Field>
                    <Form.Field>
                        <label>Dealer Order Number</label>
                        <p
                            style={{
                                color: 'rgba(0,0,0,0.65)',
                                marginBottom: 8,
                            }}
                        >
                            Enter your internal purchase order number(optional)
                        </p>
                    </Form.Field>
                    <Form.Field width={4}>
                        <Form.Input
                            name="DealerOrderNumber"
                            placeholder="Dealer Order Number"
                            value={state.gen1Order?.DealerOrderNumber ?? ''}
                            onChange={onFormChange}
                            error={!!state.validationErrors.dealerOrderNumber}
                            maxLength={MAX_LENGTH}
                        />
                    </Form.Field>
                    <Form.Group>
                        <Form.Field
                            required
                            error={!!state.validationErrors.systemType}
                        >
                            <label>System Type</label>
                            <Form.Group>
                                <Form.Radio
                                    label="New System"
                                    name="SystemType"
                                    value={SYSTEM_TYPE_NEW}
                                    checked={
                                        state.gen1Order?.SystemType ===
                                        SYSTEM_TYPE_NEW
                                    }
                                    onChange={onSystemTypeChange}
                                />
                                <Form.Radio
                                    label="Existing System"
                                    name="SystemType"
                                    value={SYSTEM_TYPE_EXISTING}
                                    checked={
                                        state.gen1Order?.SystemType ===
                                        SYSTEM_TYPE_EXISTING
                                    }
                                    onChange={onSystemTypeChange}
                                />
                            </Form.Group>

                            {state.gen1Order?.SystemType ===
                                SYSTEM_TYPE_EXISTING && (
                                <Form.Field
                                    required
                                    error={!!state.validationErrors.systemId}
                                    style={{ marginBottom: 18 }}
                                >
                                    <label>System ID</label>
                                    <Form.Input
                                        name="SystemId"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        placeholder="Enter System ID (numbers only)"
                                        value={state.gen1Order?.SystemId}
                                        onChange={onFormChange}
                                        error={
                                            !!state.validationErrors.systemId
                                        }
                                        maxLength={MAX_LENGTH}
                                    />
                                </Form.Field>
                            )}
                        </Form.Field>
                    </Form.Group>
                    {isPurchasedApplicationType && (
                        <Gen1ApplicationPurchasedTrunking
                            gen1Order={state.gen1Order}
                            orderNumber={state.orderNumber ?? ''}
                            otherSystemId={state.otherSystemId ?? ''}
                            addRepeater={state.addRepeater}
                            orderSearchLoading={!!orderSearchLoading}
                            orderSearchResult={orderSearchResult ?? []}
                            orderSearchError={orderSearchError ?? ''}
                            orderSearchModalOpen={!!orderSearchModalOpen}
                            orderNumberSearchSectionRef={
                                orderNumberSearchSectionRef
                            }
                            validationErrors={state.validationErrors}
                            onFormChange={onFormChange}
                            onSearchClick={onSearchClick}
                            onOrderSearchShowModalClick={
                                onOrderSearchShowModalClick
                            }
                            onOrderSearchModalClose={onOrderSearchModalClose}
                            onOrderSearchSelectAllChange={
                                onOrderSearchSelectAllChange
                            }
                            onOrderSearchRowToggle={onOrderSearchRowToggle}
                            onOrderSearchRegisterClick={
                                onOrderSearchRegisterClick
                            }
                            onAddRepeaterRowClick={handleAddRepeaterRow}
                            onRepeaterLineChange={handleRepeaterLineChange}
                            onDeleteManualRepeaterRow={
                                handleDeleteManualRepeaterRow
                            }
                            onDeleteSearchRepeaterRow={
                                onDeleteSearchRepeaterRow
                            }
                            onDeleteRemoveRepeaterRow={
                                handleDeleteRemoveRepeaterRow
                            }
                            onDeleteExchangeRepeaterPair={
                                handleDeleteExchangeRepeaterPair
                            }
                        />
                    )}

                    {!isPurchasedApplicationType && (
                        <Gen1ApplicationNewPurchaseTrunking
                            gen1Order={state.gen1Order}
                            validationErrors={state.validationErrors}
                            onFormChange={onFormChange}
                            onRepeaterLineChange={handleRepeaterLineChange}
                            onDeleteManualRepeaterRow={
                                handleDeleteManualRepeaterRow
                            }
                        />
                    )}

                    <Header
                        as="h4"
                        content="USB Keys and Software"
                        style={{ marginTop: 24 }}
                    />
                    <Form.Field
                        error={
                            !!(
                                state.validationErrors.usbKeyKpgkey01OrSm &&
                                !state.gen1Order?.Gen1TrunkingOrderItem
                                    ?.isNeededUsbKeyAndSoftware
                            )
                        }
                    >
                        <Form.Checkbox
                            label="Add Key"
                            name="isNeededUsbKeyAndSoftware"
                            checked={
                                state.gen1Order?.Gen1TrunkingOrderItem
                                    ?.isNeededUsbKeyAndSoftware ?? false
                            }
                            onChange={onFormChange}
                        />
                    </Form.Field>
                    {state.gen1Order?.Gen1TrunkingOrderItem
                        ?.isNeededUsbKeyAndSoftware && (
                        <Form.Group grouped style={{ marginLeft: '1em' }}>
                            {(() => {
                                const item =
                                    state.gen1Order?.Gen1TrunkingOrderItem ??
                                    {};
                                const ve = state.validationErrors;
                                return USB_KEY_ITEM_FIELDS.map((itemField) => {
                                    const display = USB_KEY_DISPLAY[itemField];
                                    const quantity =
                                        Number(item[itemField]) || 0;
                                    const isK01OrSm =
                                        itemField ===
                                            'Grade1UnlimitedAccessUsbKeyQty' ||
                                        itemField ===
                                            'SystemManagerSoftwareWithGrade1KeyQty';
                                    const usbInputError =
                                        !!ve[`usbKey_${itemField}`] ||
                                        !!(
                                            ve.usbKeyKpgkey01OrSm &&
                                            item.isNeededUsbKeyAndSoftware &&
                                            isK01OrSm
                                        );
                                    return (
                                        <Form.Field
                                            key={itemField}
                                            style={{ marginBottom: 12 }}
                                        >
                                            <label>{`${display?.code ?? ''} — ${display?.label ?? ''}`}</label>
                                            <Form.Input
                                                name={itemField}
                                                type="number"
                                                min={0}
                                                max={USB_KEY_MAX[itemField]}
                                                value={quantity}
                                                onChange={onFormChange}
                                                placeholder="Enter quantity"
                                                width={4}
                                                error={usbInputError}
                                                style={{ marginTop: 6 }}
                                            />
                                        </Form.Field>
                                    );
                                });
                            })()}
                        </Form.Group>
                    )}
                    <Form.TextArea
                        label="Additional Notes"
                        name="AdditionalNotes"
                        placeholder="Enter Additional Notes"
                        value={state.gen1Order?.AdditionalNotes ?? ''}
                        onChange={onFormChange}
                        error={!!state.validationErrors.additionalNotes}
                        rows={4}
                    />
                    <p style={{ marginTop: 0, color: '#666' }}>
                        {notesLength} / {ADDITIONAL_NOTES_MAX_LENGTH} characters
                    </p>
                </Form>
            </Segment>

            {actionButtons}

            <Gen1ApplicationPreviewModal
                open={state.previewModalOpen}
                onClose={() =>
                    setState((prev) => ({ ...prev, previewModalOpen: false }))
                }
                applicationState={state}
                onOrderClick={handleSubmitOrderFromPreview}
                isSubmitting={Reducer.gen1OrderSaveFetching}
                orderDisabled={Reducer.gen1MasterFetchingError}
            />
            <SaveCompleteModal
                open={state.completeModalOpen}
                isOrdered={isGen1NonDraftOrderSaveStatus(
                    Reducer.gen1OrderSaveLastStatus
                )}
                onCloseCompleteModal={onCloseCompleteModal}
                onGoToHistory={onGoToHistoryFromComplete}
            />
            <Dimmer active={Reducer.gen1OrderSaveFetching} page>
                <Loader content="Loading..." size="huge" />
            </Dimmer>
        </>
    );
}

/** 保存/申請完了モーダル（ProtectionPlan の SaveCompleteModal と同様） */
function SaveCompleteModal({
    open,
    isOrdered,
    onCloseCompleteModal,
    onGoToHistory,
}) {
    return (
        <Modal dimmer open={open} size="tiny">
            <Modal.Header>
                <Header
                    icon="check"
                    content={
                        isOrdered
                            ? 'Application submitted!'
                            : 'Saving is completed!'
                    }
                />
            </Modal.Header>
            <Modal.Content>
                <Header as="h3">Please select the next operation.</Header>
                <Segment compact basic className="margin-auto">
                    <Button
                        color="blue"
                        content={
                            isOrdered
                                ? 'Start a new application'
                                : 'Continue editing'
                        }
                        onClick={onCloseCompleteModal}
                        className="margin-btm display-block"
                    />
                    <Button
                        color="blue"
                        content="Go to History screen"
                        onClick={onGoToHistory}
                        className="margin-btm display-block"
                    />
                </Segment>
            </Modal.Content>
        </Modal>
    );
}
